import React from 'react';
import {
  Controller, SubmitHandler, useFieldArray, useFormContext,
} from 'react-hook-form';
import {
  Box,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import {
  ErrorMessage,
  PrimaryLoadingButton,
  PrimaryOutlinedButton,
  PrimaryDatePicker,
  PrimaryTimePicker,
  PrimaryTextField,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { SessionAudienceTypes } from 'config';
import { IEventLocationsForm, CourseOnlySessionStatus } from '../../../../interface';
import { eventLocationsFieldConfigs } from '../../validation/event-locations-validation';

interface EventLocationsFormProps {
  onSubmit: SubmitHandler<IEventLocationsForm>;
  loading: boolean;
  audienceType: string;
  courseOnlyStatus: string;
}

const EventLocationsForm = ({
  onSubmit,
  loading,
  audienceType,
  courseOnlyStatus,
}: EventLocationsFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormContext<IEventLocationsForm>();

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'locations',
  });

  const handleAddLocation = () => {
    if (fields.length >= 7) {
      return;
    }
    append({
      date: '',
      from: '',
      to: '',
      address: '',
    });
  };

  const handleRemoveLocation = (index: number) => {
    if (fields.length <= 1) {
      return;
    }
    remove(index);
  };

  const getMinTimeForLocation = (index: number) => {
    const dateValue = watch(`locations.${index}.date`);
    if (!dateValue) return undefined;

    const selectedDate = moment(dateValue, 'YYYY-MM-DD', true);
    const today = moment().startOf('day');

    if (selectedDate.isAfter(today)) {
      return undefined;
    }

    if (selectedDate.isSame(today)) {
      const now = moment();
      const roundedNow = now.clone().add(1, 'minute').startOf('minute');
      return roundedNow;
    }

    return undefined;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} className="form-item">
        {fields.map((field, index) => (
          <Grid item xs={12} key={field.id}>
            <Typography fontWeight={600}>{`Location ${index + 1}`}</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`locations.${index}.date`}
                  render={({ field: controllerField }) => (
                    <PrimaryDatePicker
                      {...controllerField}
                      label="Date"
                      placeholder="Select date"
                      value={controllerField.value ? moment(controllerField.value, 'YYYY-MM-DD') : null}
                      onChange={(value: any) => {
                        controllerField.onChange(value ? moment(value).format('YYYY-MM-DD') : '');
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.date?.message}</ErrorMessage>
              </Grid>
              <Grid item xs={2.5}>
                <Controller
                  control={control}
                  name={`locations.${index}.from`}
                  render={({ field: controllerField }) => (
                    <PrimaryTimePicker
                      {...controllerField}
                      label="From"
                      value={controllerField.value ? moment(controllerField.value, 'HH:mm:ss') : null}
                      minTime={getMinTimeForLocation(index)}
                      onChange={(value: any) => {
                        controllerField.onChange(value ? moment(value).format('HH:mm:ss') : '');
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.from?.message}</ErrorMessage>
              </Grid>
              <Grid item xs={2.5}>
                <Controller
                  control={control}
                  name={`locations.${index}.to`}
                  render={({ field: controllerField }) => (
                    <PrimaryTimePicker
                      {...controllerField}
                      label="To"
                      value={controllerField.value ? moment(controllerField.value, 'HH:mm:ss') : null}
                      minTime={getMinTimeForLocation(index)}
                      onChange={(value: any) => {
                        controllerField.onChange(value ? moment(value).format('HH:mm:ss') : '');
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.to?.message}</ErrorMessage>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  control={control}
                  name={`locations.${index}.address`}
                  render={({ field: controllerField }) => (
                    <>
                      <PrimaryTextField
                        {...controllerField}
                        label="Address"
                        placeholder="Enter address"
                      />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>
                          {(errors as any)?.locations?.[index]?.address?.message}
                        </ErrorMessage>
                        <CharacterCountDisplay
                          value={controllerField.value || ''}
                          config={eventLocationsFieldConfigs.address}
                        />
                      </Box>
                    </>
                  )}
                />
              </Grid>
            </Grid>
            {fields.length > 1 && (
              <>
                <PrimaryOutlinedButton
                  type="button"
                  onClick={() => handleRemoveLocation(index)}
                >
                  Remove
                </PrimaryOutlinedButton>
                <Grid item xs={8}>
                  <Divider />
                </Grid>
              </>
            )}
          </Grid>
        ))}
      </Grid>

      <Grid container justifyContent="flex-start" className="form-item" spacing={2}>
        <Grid item>
          <PrimaryOutlinedButton type="button" onClick={handleAddLocation} disabled={fields.length >= 7}>
            <AddIcon sx={{ marginRight: '0.5rem' }} />
            Add location
          </PrimaryOutlinedButton>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} type="submit">
            {audienceType === SessionAudienceTypes.courseOnly
              ? courseOnlyStatus !== CourseOnlySessionStatus.editing
                ? 'Add to course'
                : 'Update session'
              : 'Save & continue'}
          </PrimaryLoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default EventLocationsForm;
